"use client";

import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Command, CommandGroup, CommandItem } from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

// Supported languages configuration
const languages = [
  { code: "it", name: "Italiano", flag: "it" },
  { code: "en", name: "English", flag: "gb" },
];

interface Language {
  code: string;
  name: string;
  flag: string;
}

const LanguageLabel = ({ language }: { language: Language }) => (
  <span className="flex items-center gap-2">
    <span className={`fi fi-${language.flag} rounded-xs`}></span>
    {language.name}
  </span>
);

export function LanguageSelector() {
  const { t, i18n } = useTranslation("common");
  const [open, setOpen] = React.useState(false);

  const currentLanguage =
    languages.find((lang) => lang.code === i18n.language) || languages[0];

  const handleLanguageChange = (language: Language) => {
    i18n.changeLanguage(language.code);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          role="combobox"
          aria-expanded={open}
          className="bg-transparent"
        >
          <span
            className={`fi fi-${currentLanguage.flag} rounded-xs text-xs`}
          ></span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-48">
        <Command shouldFilter={false}>
          <CommandGroup>
            {languages.map((language) => (
              <CommandItem
                key={language.code}
                value={language.code}
                onSelect={() => handleLanguageChange(language)}
              >
                <Check
                  className={`mr-2 h-4 w-4 ${
                    i18n.language === language.code
                      ? "opacity-100"
                      : "opacity-0"
                  }`}
                />
                <LanguageLabel language={language} />
              </CommandItem>
            ))}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
